import { useContext, useRef, useState } from "react";
import { fetchGpt4API } from "../../services/apiService";
import { extractJSONFromString } from "../../utils/helpers";
import { CategoryAnalysisContext } from "./category-analysis-context";

const MAX_COMPETITORS = 15;

function generateCategoryCompetitorPrompt(category, country = "United States", type = "") {
  const entityPhrase = type ? `${category} ${type}` : category;

  return `
    Based on the specified category "${category}" and market in "${country}", provide a list of the top ${MAX_COMPETITORS} ${entityPhrase} ranked in order of market presence and consumer recognition.

    ### Instructions:
    - Output the response in the following JSON format:
    {
      "data": [
        { "name": "[Name]", "domain": "url.com" },
        ...
      ]
    }
    - Each name should strictly follow the format: **"[Name]"**.
    - Each item should have its primary website domain listed in the "domain" field.
    - Only include items that fit within the specified category: "${category}".
    - Rankings should reflect overall market presence and consumer recognition in ${country}.
    - Consider items that are officially available in ${country}.
    - Do not include placeholders or incomplete fields in the output.
    - Return exactly ${MAX_COMPETITORS} items if possible.
  `;
}

export default function useCompetitorSuggestions() {
  const [loading, setLoading] = useState(false);
  const [keyPlayers, setKeyPlayers] = useState({});
  const ctx = useContext(CategoryAnalysisContext);
  // Per-category request counter so parallel fetches (multiple categories at once)
  // don't cancel each other. Each category only drops its own stale responses.
  const requestIdRef = useRef({});

  async function getCompetitorsForCategory(category, market = "General Market", type = "") {
    requestIdRef.current[category] = (requestIdRef.current[category] ?? 0) + 1;
    const requestId = requestIdRef.current[category];
    const abortController = new AbortController();

    setLoading(true);
    const prompt = generateCategoryCompetitorPrompt(category, market, type);

    try {
      let response = await fetchGpt4API(prompt, abortController, 1500, 1, "gpt-4o");
      response = await extractJSONFromString(response["GPT-4"][0]);

      if (response?.data && requestId === requestIdRef.current[category]) {
        const players = response.data.slice(0, MAX_COMPETITORS).map((player) => ({
          ...player,
          value: player.name,
          name: player.name,
          variants: [],
        }));

        setKeyPlayers((prev) => ({ ...prev, [category]: players }));
        return players;
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("useCompetitorSuggestions error:", error);
      }
    } finally {
      if (requestId === requestIdRef.current[category]) {
        setLoading(false);
      }
    }

    return [];
  }

  return { keyPlayers, setKeyPlayers, loading, getCompetitorsForCategory };
}
